/** Fixed, low-cardinality analytics payload. Never pass form fields, IDs or URLs. */
export const FUNNEL_STAGES = ['form_started', 'account_ready', 'verification_sent', 'verified', 'club_ready', 'dashboard_entered', 'setup_step_confirmed', 'setup_completed', 'reservation_started', 'first_reservation_created', 'error'] as const
export type FunnelStage = typeof FUNNEL_STAGES[number]
export type FunnelParams = Record<string, string | number | boolean>
const allowed: Record<string, readonly unknown[]> = {
  surface: ['web', 'app'], environment: ['dev', 'prod'], lang: ['en', 'es'],
  method: ['email', 'email_login', 'google', 'handoff', 'resume'],
  step: ['courts', 'hours', 'prices'], recovered: [true, false],
  category: ['validation', 'network', 'credentials', 'popup', 'rate_limit', 'permission', 'expired_link', 'service', 'unknown'],
  at: FUNNEL_STAGES,
}
export function funnelPayload(stage: FunnelStage, input: Record<string, unknown>): FunnelParams | null {
  if (!FUNNEL_STAGES.includes(stage)) return null
  const result: FunnelParams = { stage, schema_version: 1 }
  for (const [key, values] of Object.entries(allowed)) {
    const value = input[key]
    if (values.includes(value)) result[key] = value as string | boolean
  }
  return result
}
export function funnelError(cause: unknown): string {
  const code = typeof cause === 'object' && cause !== null && 'code' in cause ? cause.code : null
  const categories: Record<string, string> = {
    'unavailable': 'network', 'permission-denied': 'permission', 'resource-exhausted': 'rate_limit',
    'auth/network-request-failed': 'network', 'functions/unavailable': 'network',
    'auth/invalid-credential': 'credentials', 'auth/email-already-in-use': 'credentials',
    'auth/weak-password': 'validation', 'auth/invalid-email': 'validation',
    'auth/popup-blocked': 'popup', 'auth/popup-closed-by-user': 'popup',
    'functions/resource-exhausted': 'rate_limit', 'auth/too-many-requests': 'rate_limit',
    'functions/permission-denied': 'permission', 'functions/unauthenticated': 'permission',
    'auth/expired-action-code': 'expired_link', 'auth/invalid-action-code': 'expired_link',
    'functions/internal': 'service', 'functions/failed-precondition': 'service',
  }
  return typeof code === 'string' ? categories[code] ?? 'unknown' : 'unknown'
}
/** Keys stay in memory only and never enter the event. No pre-consent replay. */
export function createFunnelTracker(send: (params: FunnelParams) => boolean | Promise<boolean>, context: FunnelParams) {
  const sent = new Set<string>()
  const pending = new Set<string>()
  return (stage: FunnelStage, input: Record<string, unknown> = {}, onceKey?: string): void => {
    const key = onceKey === undefined ? undefined : `${stage}:${onceKey}`
    if (key && (sent.has(key) || pending.has(key))) return
    const payload = funnelPayload(stage, { ...context, ...input })
    if (!payload) return
    if (key) pending.add(key)
    // Defer and catch both synchronous and asynchronous SDK failures.
    void Promise.resolve().then(() => send(payload)).then(delivered => {
      if (key && delivered) sent.add(key)
    }).catch(() => {}).finally(() => { if (key) pending.delete(key) })
  }
}

/** Native validation emits one invalid event per field in the same task. */
export function createValidationReporter(report: () => void): () => void {
  let reporting = false
  return () => {
    if (reporting) return
    reporting = true
    setTimeout(() => { reporting = false }, 0)
    report()
  }
}
